import type { AttemptResult } from '../levels/Evaluator';
import { getLevel } from '../levels';
import type { UserProgress } from './StorageProvider';

export interface ApplyResultOutcome {
  xpEarned: number;
  newLevelUnlocks: string[];
  newCarUnlocks: string[];
  newBadges: string[];
}

const MAX_STORED_ATTEMPTS = 40;

export const RANK_THRESHOLDS: Array<{ key: string; xp: number }> = [
  { key: 'levels.rank.expert', xp: 700 },
  { key: 'levels.rank.solid', xp: 450 },
  { key: 'levels.rank.learner', xp: 200 },
  { key: 'levels.rank.rookie', xp: 0 },
];

export function rankKeyFor(xp: number): string {
  for (const r of RANK_THRESHOLDS) if (xp >= r.xp) return r.key;
  return 'levels.rank.rookie';
}

/**
 * Aplica el resultado de un intento al progreso. Muta una copia y la devuelve.
 * La XP nunca premia la velocidad: deriva del xpReward del nivel y de la nota.
 */
export function applyAttempt(
  progress: UserProgress,
  result: AttemptResult,
  now: number,
): { progress: UserProgress; outcome: ApplyResultOutcome } {
  const p: UserProgress = structuredClone(progress);
  const level = getLevel(result.levelId);
  const outcome: ApplyResultOutcome = {
    xpEarned: 0,
    newLevelUnlocks: [],
    newCarUnlocks: [],
    newBadges: [],
  };

  p.attempts.push({
    levelId: result.levelId,
    passed: result.passed,
    metrics: result.metrics,
    ts: now,
  });
  if (p.attempts.length > MAX_STORED_ATTEMPTS) p.attempts = p.attempts.slice(-MAX_STORED_ATTEMPTS);

  const prevBest = p.bestScores[result.levelId] ?? 0;
  if (result.passed && result.metrics.score > prevBest)
    p.bestScores[result.levelId] = result.metrics.score;

  if (result.passed) {
    const firstPass = !progress.attempts.some((a) => a.levelId === result.levelId && a.passed);
    const bonus = result.metrics.stars >= 3 ? 1.2 : 1;
    outcome.xpEarned = Math.round(level.xpReward * (firstPass ? 1 : 0.2) * bonus);
    p.xp += outcome.xpEarned;

    for (const unlock of level.unlocks) {
      if (unlock.startsWith('car:')) {
        const carId = unlock.slice(4);
        if (!p.unlockedCars.includes(carId)) {
          p.unlockedCars.push(carId);
          outcome.newCarUnlocks.push(carId);
        }
      } else if (!p.unlockedLevels.includes(unlock)) {
        p.unlockedLevels.push(unlock);
        outcome.newLevelUnlocks.push(unlock);
      }
    }

    const addBadge = (id: string): void => {
      if (!p.badges.includes(id)) {
        p.badges.push(id);
        outcome.newBadges.push(id);
      }
    };
    if (firstPass) addBadge(`pass-${level.id}`);
    if (result.metrics.smoothness >= 95) addBadge('smooth-driver');
    if (result.metrics.observation >= 100 && level.order >= 4) addBadge('all-mirrors');
    if (level.id === 'l5-reverse') addBadge('graduate-basics');
    // Insignias del itinerario manual
    if (level.track === 'manual' && result.metrics.stalls === 0 && level.order >= 102)
      addBadge('no-stall');
    if (level.id === 'm6-hill' && result.metrics.rollbackM <= 0.1) addBadge('hill-master');
    if (level.id === 'm7-exam') addBadge('graduate-manual');
  }

  return { progress: p, outcome };
}
